import Bestandsaufnahme from '@/models/ba/Bestandsaufnahme';
import { APhoto } from "@/models/photo/a-photo.model";
import CachedPhoto from "@/models/photo/cached-photo.model";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { isPlatform } from "@ionic/vue";
import { instanceOfPhoto } from "./get-media-url";
import { Monitoring } from './monitoring';
import { generateUUID } from '@ionic/cli/lib/utils/uuid';

export const b64toBlob = (b64Data: any, contentType='', sliceSize=512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, {type: contentType});
    return blob;
}

export const convertBlobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
    });
}

export const savePictureLocal = async (photo: CachedPhoto): Promise<any> => {
    let base64Data: string;
    // iOS and Android
    if (isPlatform('hybrid')) {
        const file = await Filesystem.readFile({
            path: photo.path!
        });
        base64Data = file.data as string;
    } else { // Web
        // Fetch the photo, read as a blob, then convert to base64 format
        const response = await fetch(photo.webPath!);
        const blob = await response.blob();
        base64Data = await convertBlobToBase64(blob);
    }

    const randomName = Math.random().toString(36).substr(2, 12) + "." + photo.format;

    // save base64 string on data path as filename
    const savedFile = await Filesystem.writeFile({
        path: randomName,
        data: base64Data,
        directory: Directory.Data
    });

    if (isPlatform('hybrid')) {
        // Display the new image by rewriting the 'file://' path to HTTP
        // Details: https://ionicframework.com/docs/building/webview#file-protocol
        photo.filepath = savedFile.uri;
        photo.webPath = Capacitor.convertFileSrc(savedFile.uri);

    } else {
        // Use webPath to display the new image instead of base64 since it's
        // already loaded into memory
        photo.filepath = randomName;
    }
};

const loadSaved = async (photo: CachedPhoto) => {
    // If running on the web...
    if (!isPlatform('hybrid') && photo.filepath) {
        const file = await Filesystem.readFile({
            path: photo.filepath,
            directory: Directory.Data
        });

        photo.webPath = URL.createObjectURL(b64toBlob(file.data as string, 'image/jpeg'));
    }
}

export const deletePhotoLocal = async (photo: CachedPhoto) => {
    if (photo.filepath) {
        try {
            const filename = photo.filepath.substr(photo.filepath.lastIndexOf('/') + 1);
            await Filesystem.deleteFile({
                path: filename,
                directory: Directory.Data
            });
        } catch (err: any) {
            // Duplicated case can happen when cloning media in offline mode through copyFragenblock functionality
            // => same blob is re-used and uploaded twice
            // Leave one handling for now and observe in sentry dashboard
            Monitoring.chainError('deletePhotoLocal failed/attempt to remove duplicated photo', err);
        }
    }
};


/**
 * Neither Locally persisted nor uploaded image
 **/
const isImageLocalOnly = (image: APhoto) => {
    return instanceOfPhoto(image) && !image.filepath
}

export const saveHzbaPhotosLocal = (ba: Bestandsaufnahme) => {
    return new Promise((resolve, reject) => {

        // const photosUnsaved1 = ba.getFrages().map<CachedPhoto[]>(frage => (frage.eingabeMedien?.filter(image => instanceOfPhoto(image) && !image.filepath) as CachedPhoto[])).filter(a => !!a).flat(1);
        const photosUnsaved = (ba.filterPhotos(isImageLocalOnly) as CachedPhoto[]);

        // Monitoring.consoleOnly("Save unsaved/local photos: ",photosUnsaved);

        try {
            Promise.all(photosUnsaved.map(async photo => {
                await savePictureLocal(photo)
            })).then(el => {
                resolve('stored images and wrote the path to the variables.')
            }).catch(err => Monitoring.chainError("Error in saveHzbaPhotosLocal (Promise)", err));
            return ba;
        } catch (error: any) {
            Monitoring.chainError("Error in saveHzbaPhotosLocal", error);
            reject()
        }

    })
}

export const loadHzbaPhotosLocal = async (hzba: any) => {

    const proms: Promise<any>[] = [];

    const recursiveSearchPhotos = (obj: any, prefix: string) => {
        if (instanceOfPhoto(obj) && obj.filepath) {    // obj is photo and not saved locally
            // Monitoring.consoleOnly("Load local image at", prefix);
            proms.push(loadSaved(obj));
        } else {
            Object.keys(obj).forEach((key) => {
                if (obj[key] && typeof obj[key] == "object") {
                    recursiveSearchPhotos(obj[key], prefix + "." + key)
                }
            });
        }
    }

    recursiveSearchPhotos(hzba, "hzba");

    return Promise.all(proms);
}

export const createCachedPhotoFromB64 = async (b64img: string, filetype?: string, localFileName?: string, dir?: Directory, filePath?: string): Promise<CachedPhoto[]> => {
    let b64Data: string;
    let contentType: string;

    // HzbaPhotoInput / openGallery / onEdited
    if(filetype === 'png' || filetype === 'image/png' || b64img.startsWith("data:image/png;base64,")) {
        filetype = 'png';
        b64Data = b64img.replace("data:image/png;base64,",""); // remove the prefix
        contentType = 'image/png';

    // usePhotoRecording / getCompressedFiles
    }else if (filetype === 'jpeg' || filetype === 'image/jpeg' || b64img.startsWith("data:image/jpeg;base64,")) {
        filetype = 'jpeg';
        b64Data = b64img.replace("data:image/jpeg;base64,",""); // remove the prefix
        contentType = 'image/jpeg';

    // usePhotoRecording / appendFileToFormData
    }else{
        b64Data = b64img;
        contentType = `application/${filetype}`
    }

    const uuid = generateUUID();
    const fileName = localFileName ?? uuid + '.' + filetype;

    const blob = b64toBlob(b64Data, contentType);
    const webpath = URL.createObjectURL(blob);

    return await createCachedPhoto(b64img, filetype, fileName, webpath, dir, filePath);
}

// usePhotoRecording / appendFileToFormData
export const createCachedPhotoFromBlob = async (blob: Blob, localFileName: string, filetype?: string): Promise<CachedPhoto[]> => {
    const b64Data = await convertBlobToBase64(blob);
    const webpath = URL.createObjectURL(blob);

    return await createCachedPhoto(b64Data, filetype, localFileName, webpath);
}

const createCachedPhoto = async (fileData: string, fileType: string | undefined, fileName: string, webPath: string, dir?: Directory, filePath?: string): Promise<CachedPhoto[]> => {
    const savedFile = await Filesystem.writeFile({
        path: fileName,
        data: fileData,
        directory: dir ?? Directory.Data,
    });
    const savedpath = savedFile.uri;
    const newPhoto = [
        {
            filepath: filePath,
            format: fileType,
            webPath: webPath,
            uid: generateUUID(),
            path: savedpath
        } as CachedPhoto,
    ];

    return newPhoto;
}

